import { fetchNextAnalysis } from '@services/analysisItems';
import { fetchHealthStatus } from '@services/health';
import { fetchShareTeamMembers, fetchTeamMembers } from '@services/member';
import { fetchReport, fetchReports } from '@services/report';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import _ from 'lodash';
import {
  fetchAIParticipantById,
  fetchAIParticipants,
  fetchAIParticipantTags,
} from '../services/ai-participant';
import { verifyLinkService } from '../services/auth';
import {
  fetchAssignedTag,
  fetchCodeDetails,
  fetchCodeQuotes,
  fetchInterview,
  fetchInterviewAnalysis,
  fetchInterviewCodebook,
  fetchInterviewDetails,
  fetchInterviews,
  fetchLatestChatId,
  fetchLensSummaries,
  fetchStudyTags,
  fetchTranscriptDetails,
  fetchTranscriptList,
  fetchTranscriptMetaAnalysis,
  fetchTranscriptSummary,
  getChatHistory,
  getChatHistoryById,
  getInterviewReport,
  getTranscriptRecording,
} from '../services/interview';
import { fetchNotifications } from '../services/notification';
import { getOrganization as getOrg, listOrganizations } from '../services/organization';
import {
  getOrgProduct as getProduct,
  listOrgProducts,
  listOrgProductsAll,
} from '../services/orgProduct';
import { fetchSubscriptionProducts } from '../services/payment';
import {
  fetchInterviewDetailsAnalysis,
  fetchInterviewParticipant,
  fetchParticipantCountries,
  fetchParticipantDevice,
  fetchParticipantResponseById,
  fetchQuestionCodes,
  fetchQuestionResponses,
  fetchQuestionSentimentStat,
  fetchQuestionTheme,
  fetchQuestionTranslations,
  fetchSurveyTranslationLanguages,
  fetchRecentStudies,
  fetchSharedStudies,
  fetchSingleInterviewParticipant,
  fetchSourceAndContext,
  fetchStudies,
  fetchStudy,
  fetchStudyReports,
  fetchSurveyDetails,
  fetchSurveyDetailsAnalysis,
  fetchSurveyFilterContext,
  fetchSurveyParticipant,
  fetchSurveyQuestionAnalysis,
  fetchSurveyQuestionAnalysisById,
  fetchSurveyQverallAnalysis,
} from '../services/survey';
import { fetchUser } from '../services/user';
import {
  getWorkspace,
  getWorkspaceMembers,
  getWorkspaces,
  getWorkspaceStatistics,
  getWorkspaceTransactions,
  getWorkspaceUsage,
} from '../services/workspace';

export const useNotifications = (status = '', limit = 6, options = {}) => {
  const {
    enabled = true,
    refetchOnWindowFocus = true,
    pollOnProcessing = true,
    pollIntervalMs = 15000,
  } = options;
  return useInfiniteQuery({
    queryKey: ['notifications', status, limit],
    queryFn: async ({ pageParam = 0 }) => {
      const response = await fetchNotifications(pageParam, limit, status);
      const notifications = [...(response.data || [])].sort(
        (a, b) => new Date(b.created_time) - new Date(a.created_time),
      );

      return {
        notifications,
        ...response,
      };
    },
    getNextPageParam: (lastPage) => {
      const nextOffset = lastPage.offset + lastPage.limit;
      return nextOffset >= lastPage.total ? undefined : nextOffset;
    },
    enabled,
    refetchOnWindowFocus,
    refetchIntervalInBackground: false,
    staleTime: pollIntervalMs,
    // Poll every 3 seconds when there are notifications being processed
    refetchInterval: (query) => {
      if (!enabled || !pollOnProcessing) {
        return false;
      }
      const pages = query.state.data?.pages || [];
      const hasProcessing = pages.some((page) =>
        page.data?.some((notification) => {
          const analyzedStatus = notification.analyzed_status?.toLowerCase();
          return (
            analyzedStatus === 'processing' ||
            analyzedStatus === 'analysing' ||
            analyzedStatus === 'analyzing'
          );
        }),
      );
      return hasProcessing ? pollIntervalMs : false;
    },
  });
};

export const useNotificationsUnread = (limit, status = 'unread', options = {}) => {
  const {
    enabled = true,
    refetchOnWindowFocus = true,
    pollOnProcessing = true,
    pollIntervalMs = 15000,
  } = options;
  return useInfiniteQuery({
    queryKey: ['notifications-unread', limit, status],
    queryFn: async ({ pageParam = 0 }) => {
      const response = await fetchNotifications(pageParam, limit, status);
      return response;
    },
    getNextPageParam: (lastPage) => {
      const nextOffset = lastPage.offset + lastPage.limit;
      return nextOffset >= lastPage.total ? undefined : nextOffset;
    },
    enabled: !!limit && !!status && enabled,
    refetchOnWindowFocus,
    refetchIntervalInBackground: false,
    staleTime: pollIntervalMs,
    // Poll every 3 seconds when there are notifications being processed
    refetchInterval: (query) => {
      if (!enabled || !pollOnProcessing) {
        return false;
      }
      const pages = query.state.data?.pages || [];
      const hasProcessing = pages.some((page) =>
        page.data?.some((notification) => {
          const analyzedStatus = notification.analyzed_status?.toLowerCase();
          return (
            analyzedStatus === 'processing' ||
            analyzedStatus === 'analysing' ||
            analyzedStatus === 'analyzing'
          );
        }),
      );
      return hasProcessing ? pollIntervalMs : false;
    },
  });
};

export const useNotificationsSummary = (options = {}) => {
  const { enabled = true, refetchInterval = 30000, refetchOnWindowFocus = false } = options;
  return useQuery({
    queryKey: ['notifications-summary'],
    queryFn: async () => fetchNotifications(0, 1, 'unread'),
    enabled,
    refetchInterval,
    refetchOnWindowFocus,
    refetchIntervalInBackground: false,
    staleTime: refetchInterval,
  });
};

export const useSurveys = (paginationModel) => {
  return useQuery({
    queryKey: ['surveys', JSON.stringify(paginationModel)],
    queryFn: async () => fetchStudies(paginationModel),
    enabled: !!paginationModel,
  });
};

export const useRecentSurveys = () =>
  useSuspenseQuery({
    queryKey: ['recent_study'],
    queryFn: () => fetchRecentStudies(),
  });

export const useInterviews = (paginationModel) =>
  useQuery({
    queryKey: ['qualitative_study', JSON.stringify(paginationModel)],
    queryFn: () => fetchInterviews(paginationModel),
    enabled: !!paginationModel,
  });

export const useAllStudies = () =>
  useQuery({
    queryKey: ['all_qualitative_studies'],
    queryFn: () => fetchInterviews({ page: 1, pageSize: 1000 }),
    select: (data) => data?.items || [],
  });

export const useSingleInterview = (id, options = {}) =>
  useQuery({
    queryKey: ['interview', id],
    queryFn: () => fetchInterview(id),
    enabled: !!id,
    // Poll every 5 seconds when study is in processing/analysing state
    refetchInterval: (data) => {
      const status = data?.status?.toLowerCase?.() || '';
      const isProcessing =
        status === 'processing' ||
        status === 'analysing' ||
        status === 'analyzing' ||
        status === 'generating';
      return isProcessing ? 5000 : false;
    },
    useErrorBoundary: true,
    throwOnError: true,
    ...options,
  });

export const useStudyDetail = (id, studyType, options = {}) =>
  useQuery({
    queryKey: [studyType === 'survey' ? 'single_survey' : 'single_interview', id],
    queryFn: () => (studyType === 'survey' ? fetchSurveyDetails(id) : fetchInterview(id)),
    enabled: !!id,
    // Poll every 5 seconds when study is in processing/analysing state
    refetchInterval: (data) => {
      const status = data?.status?.toLowerCase?.() || '';
      const isProcessing =
        status === 'processing' ||
        status === 'analysing' ||
        status === 'analyzing' ||
        status === 'generating';
      return isProcessing ? 5000 : false;
    },
    onError: (error) => console.error('Error fetching single interview:', error),
    ...options,
  });

export const useSingleInterviewStatus = (id) =>
  useQuery({
    queryKey: ['single_interview_status', id],
    queryFn: async () => await fetchInterview(id),
    enabled: !!id,
    onError: (error) => console.error('Error fetching single interview:', error),
  });

export function useGetStudy(id, studyType) {
  return useQuery({
    queryKey: ['study', id, studyType],
    queryFn: () => (studyType === 'dynamic_survey' ? fetchStudy(id) : fetchInterview(id)),
    enabled: !!id && !!studyType,
  });
}

export const useSingleSurvey = (id) =>
  useSuspenseQuery({
    queryKey: ['single_survey', id],
    queryFn: () => fetchStudy(id),
  });

export const useSingleSurveyStatus = (id) =>
  useSuspenseQuery({
    queryKey: ['single_survey', id],
    queryFn: () => fetchStudy(id),
    enabled: !!id,
    onError: (error) => console.error('Error fetching single study:', error),
  });

export const useSingleStudy = (studyId, studyType) =>
  useQuery({
    queryKey: [studyType, studyId],
    queryFn: () => (studyType === 'survey' ? fetchStudy(studyId) : fetchInterview(studyId)),
    enabled: !!studyId && !!studyType,
    onError: (error) => console.error('Error fetching single study:', error),
  });

export const useProfile = () =>
  useQuery({
    queryKey: ['me'],
    queryFn: () => fetchUser(),
    // refetchOnWindowFocus: true,
    // staleTime: 60 * 60 * 24,
  });

export const useSurveyDetails = (id, options = {}) =>
  useQuery({
    queryKey: ['survey_details', id],
    queryFn: () => fetchSurveyDetails(id),
    enabled: !!id,
    // Poll every 5 seconds when survey is in processing/analysing state
    refetchInterval: (data) => {
      const status = data?.status?.toLowerCase?.() || '';
      const isProcessing =
        status === 'processing' ||
        status === 'analysing' ||
        status === 'analyzing' ||
        status === 'generating';
      return isProcessing ? 5000 : false;
    },
    ...options,
  });

export const useInterviewDetails = (id) =>
  useQuery({
    queryKey: ['interview_details', id],
    queryFn: () => fetchInterviewDetails(id),
    enabled: !!id,
  });
export const useSurveyDetailsAnalysis = ({ id }) =>
  useSuspenseQuery({
    queryKey: ['survey_details_analysis', id],
    queryFn: () => fetchSurveyDetailsAnalysis({ id }),
    enabled: !!id,
  });
export const useInterviewDetailsAnalysis = ({ id }) =>
  useSuspenseQuery({
    queryKey: ['interview_details_analysis', id],
    queryFn: () => fetchInterviewDetailsAnalysis({ id }),
    enabled: !!id,
    useErrorBoundary: true, // Add this line
    throwOnError: true,
  });

export const useSurveyQuestionAnalysis = ({ id }) =>
  useSuspenseQuery({
    queryKey: ['survey_question_analysis', id],
    queryFn: () => fetchSurveyQuestionAnalysis({ id }),
    enabled: !!id,
  });

// Organizations & Products (Org context)
export const useOrganizations = (params) =>
  useQuery({
    queryKey: ['organizations', params],
    queryFn: () => listOrganizations(params || {}),
    enabled: params !== undefined,
    keepPreviousData: true,
  });

export const useOrganization = (id) =>
  useQuery({
    queryKey: ['organization', id],
    queryFn: () => getOrg(id),
    enabled: !!id,
  });

export const useOrgProducts = (params) =>
  useQuery({
    queryKey: ['org_products', params],
    queryFn: () => listOrgProductsAll(params || {}),
    enabled: params !== undefined,
    keepPreviousData: true,
  });

export const useOrgProductsByOrg = (orgId, params) =>
  useQuery({
    queryKey: ['org_products_by_org', orgId, params],
    queryFn: () => listOrgProducts(orgId, params || {}),
    enabled: !!orgId,
    keepPreviousData: true,
  });

export const useOrgProduct = (id) =>
  useQuery({
    queryKey: ['org_product', id],
    queryFn: () => getProduct(id),
    enabled: !!id,
  });

export const useSubscriptionProducts = () =>
  useQuery({
    queryKey: ['subscription-products'],
    queryFn: fetchSubscriptionProducts,
  });
export const useMe = (isAuthenticated) =>
  useQuery({
    queryKey: ['me'],
    queryFn: () => fetchUser(),
    enabled: !!isAuthenticated,
  });
export const useVerifyLink = ({ token }) =>
  useQuery({
    queryKey: ['verifyLink'],
    queryFn: () => verifyLinkService({ token }),
    onSuccess: () => {},
    enabled: !!token,
  });
export const useAIParticipants = (paginationModel) =>
  useQuery({
    queryKey: ['ai_participants', JSON.stringify(paginationModel)],
    queryFn: () => fetchAIParticipants(paginationModel),
    enabled: !!paginationModel,
  });

export const useAIParticipantTags = () =>
  useQuery({
    queryKey: ['ai_participant_tags'],
    queryFn: fetchAIParticipantTags,
  });

export const useSurveyParticipants = (paginationModel, surveyId) =>
  useQuery({
    queryKey: ['survey_paticipants', surveyId, JSON.stringify(paginationModel)],
    queryFn: () => fetchSurveyParticipant(paginationModel, surveyId),
    enabled: !!paginationModel && !!surveyId,
    cacheTime: 0,
  });
export const useInterviewParticipants = (paginationModel, surveyId) =>
  useQuery({
    queryKey: ['interview_paticipants', surveyId, JSON.stringify(paginationModel)],
    queryFn: () => fetchInterviewParticipant(paginationModel, surveyId),
    enabled: !!paginationModel && !!surveyId,
    cacheTime: 0,
  });
export const useSingleInterviewParticipant = (participantId, surveyId) =>
  useQuery({
    queryKey: ['single_interview_participant', participantId, surveyId],
    queryFn: () => fetchSingleInterviewParticipant(participantId, surveyId),
    enabled: !!participantId && !!surveyId,
    cacheTime: 0,
  });

export const useSurveyQuestionAnalysisById = ({
  surveyId,
  questionId,
  sectionId,
  selected_questions,
}) => {
  return useQuery({
    queryKey: ['survey_question_analysis', surveyId, questionId, sectionId, selected_questions],
    queryFn: () =>
      fetchSurveyQuestionAnalysisById({ surveyId, questionId, sectionId, selected_questions }),
    enabled: !!surveyId && !!questionId && !!sectionId,
    onError: (error) => {
      console.error('Error fetching survey question analysis:', error);
    },
  });
};

export function usePreFetchSurveyQuestionAnalysisById() {
  const queryClient = useQueryClient();

  const prefetchSurveyQuestionAnalysisById = async ({
    surveyId,
    questionId,
    sectionId,
    selected_questions,
  }) => {
    // The data will be cached and available when needed
    await queryClient.prefetchQuery({
      queryKey: ['survey_question_analysis', surveyId, questionId, sectionId, selected_questions],
      queryFn: () =>
        fetchSurveyQuestionAnalysisById({ surveyId, questionId, sectionId, selected_questions }),
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    });
  };

  const mutation = useMutation({
    mutationFn: ({ surveyId, questionId, sectionId, selected_questions }) =>
      fetchSurveyQuestionAnalysisById({ surveyId, questionId, sectionId, selected_questions }),
    onSuccess: (data, variables) => {
      // Update the cache with the new data
      queryClient.setQueryData(
        [
          'survey_question_analysis',
          variables.surveyId,
          variables.questionId,
          variables.sectionId,
          variables.selected_questions,
        ],
        data,
      );
    },
  });

  return {
    prefetchSurveyQuestionAnalysisById,
    ...mutation,
  };
}

export const useSurveyQuestionAnalysisFilter = ({ surveyId, questionId, sectionId }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ surveyId, questionId, sectionId, selected_questions }) =>
      fetchSurveyQuestionAnalysisById({ surveyId, questionId, sectionId, selected_questions }),
    onMutate: async () => {
      queryClient.invalidateQueries(['survey_question_analysis']);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['survey_question_analysis', surveyId, questionId, sectionId]);
      queryClient.setQueryData(['survey_question_analysis', surveyId, questionId, sectionId], data);
    },
  });
};

export const useSurveyOverallAnalysis = ({ surveyId }) =>
  useSuspenseQuery({
    queryKey: ['overall_analysis', surveyId],
    queryFn: () => fetchSurveyQverallAnalysis({ surveyId }),
    enabled: !!surveyId,
  });

export const useInterviewOverallAnalysis = ({ interviewId, analysisItemId } = {}) =>
  useSuspenseQuery({
    // Key includes analysisItemId so navigating between two analyses
    // of the same study refetches instead of returning a stale cached
    // report for whichever was loaded first.
    queryKey: ['overall_findings', interviewId, analysisItemId || null],
    queryFn: () => getInterviewReport({ interviewId, analysisItemId }),
    enabled: !!interviewId,
  });

export const useParticipantCountries = (id) =>
  useQuery({
    queryKey: ['participant_countries', id],
    queryFn: () => fetchParticipantCountries(id),
  });

export const useParticipantDevices = (id) =>
  useQuery({
    queryKey: ['participant_devices', id],
    queryFn: () => fetchParticipantDevice(id),
  });
export const useParticipantCountriesForInterview = (id) =>
  useQuery({
    queryKey: ['participant_countries_interview', id],
    queryFn: () => fetchParticipantCountries(id),
  });

export const useParticipantDevicesForInterview = (id) =>
  useQuery({
    queryKey: ['participant_devices_interview', id],
    queryFn: () => fetchParticipantDevice(id),
  });
export const useParticipantResponse = ({ participantId, surveyId }) =>
  useSuspenseQuery({
    queryKey: ['participant_response', participantId, surveyId],
    queryFn: () => fetchParticipantResponseById({ participantId, surveyId }),
    enabled: !!participantId,
  });
export const useSourceAndContext = ({ surveyId }) =>
  useSuspenseQuery({
    queryKey: ['source_context', surveyId],
    queryFn: () => fetchSourceAndContext({ surveyId }),
    enabled: !!surveyId,
  });
export const useSurveyFilterContext = ({ surveyId }) =>
  useSuspenseQuery({
    queryKey: ['survey_filter', surveyId],
    queryFn: () => fetchSurveyFilterContext({ surveyId }),
    enabled: !!surveyId,
  });
export const useFetchAiParticipantById = ({ participantId }) =>
  useSuspenseQuery({
    queryKey: ['ai-participant', participantId],
    queryFn: () => fetchAIParticipantById({ participantId }),
    enabled: !!participantId,
    cacheTime: 0, // Disabled caching because while editing and saving and then again editing i got the same unpdated data
    staleTime: 0, // because while editing i got the same unpdated data
  });

export const useFetchTranscriptList = (
  studyId,
  filterList = [],
  limit,
  options = {},
  statusFilter,
  typeFilter,
) =>
  useInfiniteQuery({
    queryKey: ['transcripts', studyId, filterList, statusFilter, typeFilter],
    queryFn: async ({ pageParam = 0 }) => {
      const pagination = { offset: pageParam, limit: limit };
      const response = await fetchTranscriptList({
        studyId,
        filterList,
        pagination,
        statusFilter,
        typeFilter,
      });
      return response;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.pagination?.has_more
        ? lastPage.pagination.offset + lastPage.pagination.limit
        : undefined;
    },
    enabled: !!studyId,
    // Polling support: set refetchInterval to poll during processing states
    refetchInterval: options.refetchInterval || false,
  });

export const useFetchTranscriptListPagination = (
  studyId,
  filterList = [],
  pagination,
  options = {},
  statusFilter,
  sourceFilter,
) =>
  useQuery({
    queryKey: ['transcripts', studyId, filterList, pagination, statusFilter, sourceFilter],
    queryFn: async () => {
      const response = await fetchTranscriptList({
        studyId,
        filterList,
        pagination: {
          limit: pagination?.limit || 10,
          offset: pagination?.offset || 0,
        },
        statusFilter,
        sourceFilter,
      });
      return response;
    },
    enabled: options.enabled ?? !!studyId,
    // Polling support: set refetchInterval to poll during processing states
    refetchInterval: options.refetchInterval || false,
  });

export const useFetchTranscriptDetail = ({ studyId, transcriptId }) =>
  useQuery({
    queryKey: ['transcript', transcriptId, studyId],
    queryFn: () => fetchTranscriptDetails({ studyId, transcriptId }),
    enabled: !!transcriptId && !!studyId,
  });
export const useFetchTranscriptSummary = ({ transcriptId, enabled = true }) =>
  useQuery({
    queryKey: ['transcriptSummary', transcriptId],
    queryFn: () => fetchTranscriptSummary({ transcriptId }),
    enabled: !!transcriptId && enabled,
  });

// Force a fresh summary generation (bypasses the server-side cache) and writes
// the result straight into the ['transcriptSummary', transcriptId] cache so the
// open panel reflects the new output without a refetch.
export const useRegenerateTranscriptSummary = ({ transcriptId }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => fetchTranscriptSummary({ transcriptId, regenerate: true }),
    onSuccess: (data) => {
      queryClient.setQueryData(['transcriptSummary', transcriptId], data);
    },
  });
};

export const useFetchTranscriptMetaAnalysis = ({ transcriptId, enabled = true }) =>
  useQuery({
    queryKey: ['transcriptMetaAnalysis', transcriptId],
    queryFn: () => fetchTranscriptMetaAnalysis({ transcriptId }),
    enabled: !!transcriptId && enabled,
  });

export const useFetchLensSummaries = ({ transcriptId }) =>
  useQuery({
    queryKey: ['lensSummaries', transcriptId],
    queryFn: () => fetchLensSummaries({ transcriptId }),
    enabled: !!transcriptId,
  });

export const useFetchInterviewCodebook = ({ studyId, analysisItemId } = {}) =>
  useQuery({
    queryKey: ['codebook', studyId, analysisItemId || null],
    queryFn: () => fetchInterviewCodebook({ studyId, analysisItemId }),
    enabled: !!studyId,
  });
export const useFetchInterviewAnalysis = ({ studyId, analysisItemId } = {}) =>
  useSuspenseQuery({
    queryKey: ['interview-analysis', studyId, analysisItemId || null],
    queryFn: () => fetchInterviewAnalysis({ studyId, analysisItemId }),
    enabled: !!studyId,
  });
export const useFetchCodeQuotes = ({ studyId }) =>
  useSuspenseQuery({
    queryKey: ['interview-analysis-code', studyId],
    queryFn: () => fetchCodeQuotes({ studyId }),
    enabled: !!studyId,
  });

export const useReports = (paginationModel, id) => {
  return useSuspenseQuery({
    queryKey: ['reports', JSON.stringify(paginationModel + id)],
    queryFn: async () => fetchReports(paginationModel, id),
    enabled: !!paginationModel,
    keepPreviousData: true,
  });
};
export const useReport = (studyId, reportId) => {
  return useSuspenseQuery({
    queryKey: ['reports', JSON.stringify(studyId + reportId)],
    queryFn: async () => fetchReport(studyId, reportId),
    enabled: !!reportId,
  });
};

export const useFetchStudyTags = ({ studyId, searchQuery = '' }) =>
  useQuery({
    queryKey: ['study-tags', studyId, searchQuery],
    queryFn: () => fetchStudyTags({ studyId, searchQuery }),
    enabled: !!studyId,
  });

export const useFetchAssignedTags = (surveyId, searchQuery) =>
  useQuery({
    queryKey: ['fetch-assigned-tag', surveyId, searchQuery],
    queryFn: () => fetchAssignedTag(surveyId, searchQuery),
    enabled: !!surveyId,
    cacheTime: 0,
  });

export const useFetchTeamMembers = ({ studyId, studyUrlType }) => {
  return useQuery({
    queryKey: ['team-member', studyId, studyUrlType],
    queryFn: () => fetchTeamMembers({ studyId, studyUrlType }),
    enabled: !!studyId && !!studyUrlType,
  });
};
export const useInvitationAccepted = ({
  studyId,
  studyUrlType,
  Invitation,
  customer_member_id,
}) => {
  return useQuery({
    queryKey: ['invitation-accepted', studyId, studyUrlType, Invitation],
    queryFn: async () => {
      const res = await fetchTeamMembers({ studyId, studyUrlType });
      return res?.some((item) => item.user_id == customer_member_id);
    },
    enabled: !!Invitation,
  });
};

export const useFetchShareTeamMember = ({ studyId, studyUrlType, enabled = false }, open) => {
  return useQuery({
    queryKey: ['team-share-member', studyId, studyUrlType],
    queryFn: () => fetchShareTeamMembers({ studyId, studyUrlType }),
    enabled: enabled && !!studyId && !!studyUrlType && open,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
};

export const useStudyRecording = (itemId) => {
  return useQuery({
    queryKey: ['study-recording', itemId],
    queryFn: () => getTranscriptRecording(itemId),
    enabled: !!itemId,
    refetchInterval: 60000, // 1 minute
  });
};
export const useFetchChatHistory = (studyId) => {
  return useQuery({
    queryKey: ['chat-history', studyId],
    queryFn: () => getChatHistory(studyId),
    enabled: !!studyId,
  });
};
export const useFetchChatHistoryById = (chatId, newChatId, options = {}) => {
  const shouldFetch = !!chatId && newChatId !== 'new-conversation' && newChatId !== '-';
  return useQuery({
    queryKey: ['chat-history-id', chatId],
    queryFn: () => getChatHistoryById(chatId),
    enabled: shouldFetch,
    ...options,
  });
};
export const useFetchLatestChatId = (studyId) =>
  useQuery({
    queryKey: ['latest-chat-id', studyId],
    queryFn: () => fetchLatestChatId(studyId),
    enabled: !!studyId,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

// Workspace queries
export const useWorkspaces = () =>
  useQuery({
    queryKey: ['workspaces'],
    queryFn: () => getWorkspaces(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    refetchOnWindowFocus: false,
  });

export const useWorkspace = (workspaceId) => {
  const searchParams = new URLSearchParams(window.location.search);
  const token = searchParams.get('token');

  return useQuery({
    queryKey: ['workspace', workspaceId, token],
    queryFn: () => getWorkspace(workspaceId, token),
    enabled: !!workspaceId,
    staleTime: 3 * 60 * 1000, // Cache for 3 minutes
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    refetchOnWindowFocus: false,
  });
};

export const useWorkspaceMembers = (workspaceId) =>
  useQuery({
    queryKey: ['workspace-members', workspaceId],
    queryFn: () => getWorkspaceMembers(workspaceId),
    enabled: !!workspaceId,
  });

/**
 * Fetch workspace usage and entitlements.
 * Returns entitlement balances, caps, expiration info, and account representative.
 * For legacy workspaces, returns { legacy: true, message: "..." }
 */
export const useWorkspaceUsage = (workspaceId) =>
  useQuery({
    queryKey: ['workspace-usage', workspaceId],
    queryFn: () => getWorkspaceUsage(workspaceId),
    enabled: !!workspaceId,
    staleTime: 2 * 60 * 1000, // Cache for 2 minutes
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    refetchOnWindowFocus: false,
  });

/**
 * Fetch workspace transaction history (audit log of entitlement changes).
 * Only accessible to workspace owners and admins.
 * @param {string} workspaceId - The workspace ID
 * @param {Object} options - Optional query params
 * @param {number} options.limit - Max records to return (default 100)
 * @param {number} options.offset - Records to skip (default 0)
 * @param {string} options.entitlement_type - Filter by type (optional)
 * @param {boolean} enabled - Whether to enable the query (default true)
 */
export const useWorkspaceTransactions = (workspaceId, options = {}, enabled = true) =>
  useQuery({
    queryKey: ['workspace-transactions', workspaceId, options],
    queryFn: () => getWorkspaceTransactions(workspaceId, options),
    enabled: !!workspaceId && enabled,
    staleTime: 1 * 60 * 1000, // Cache for 1 minute
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    refetchOnWindowFocus: false,
  });

/**
 * Fetch aggregated workspace statistics.
 * Returns study counts, response counts, AI participant data, and recent activity.
 * @param {string} workspaceId - The workspace ID
 */
export const useWorkspaceStatistics = (workspaceId) =>
  useQuery({
    queryKey: ['workspace-statistics', workspaceId],
    queryFn: () => getWorkspaceStatistics(workspaceId),
    enabled: !!workspaceId,
    staleTime: 3 * 60 * 1000, // Cache for 3 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    refetchOnWindowFocus: false,
  });

export const useHealthStatus = () => {
  return useQuery({
    queryKey: ['health-status'],
    queryFn: fetchHealthStatus,
  });
};

export const useFetchCodeDetails = ({ studyId, codeName }) => {
  return useQuery({
    queryKey: ['code-details', codeName, studyId],
    queryFn: () => fetchCodeDetails({ studyId, codeName }),
    enabled: !!codeName && !!studyId,
  });
};

export const useFetchQuestionResponses = (surveyId, questionId, options = { limit: 12 }) => {
  const { limit = 12, searchTerm = '', searchType = 'semantic', sentiment, threshold } = options;

  return useInfiniteQuery({
    queryKey: [
      'survey-question-responses',
      surveyId,
      questionId,
      searchTerm,
      searchType,
      sentiment,
      threshold || '',
    ],
    queryFn: ({ pageParam = 0 }) =>
      fetchQuestionResponses(surveyId, questionId, {
        offset: pageParam,
        limit,
        search_term: searchTerm,
        sentiment,
        search_type: searchType,
        ...(searchType === 'semantic' && threshold !== undefined && { threshold }),
      }),
    getNextPageParam: (lastPage, allPages) => {
      // Handle case when pagination is null
      if (!lastPage.pagination) return undefined;

      const { total_count } = lastPage.pagination;
      const nextOffset = allPages.reduce((acc, page) => acc + (page.pagination?.limit || limit), 0);
      return nextOffset < total_count ? nextOffset : undefined;
    },
    getPreviousPageParam: (firstPage, allPages) => {
      if (allPages.length <= 1) return undefined;
      return allPages
        .slice(0, -1)
        .reduce((acc, page) => acc + (page.pagination?.limit || limit), 0);
    },
    enabled: !!surveyId && !!questionId,
    keepPreviousData: true,
  });
};

/**
 * Languages this survey has translated responses for (e.g. ["es", "fr"]).
 * Powers the language selector on the question-analysis page. Quick
 * Mongo aggregation server-side, cached generously here.
 */
export const useSurveyTranslationLanguages = (surveyId) =>
  useQuery({
    queryKey: ['survey-translation-languages', surveyId],
    queryFn: () => fetchSurveyTranslationLanguages(surveyId),
    enabled: !!surveyId,
    staleTime: 60 * 1000,
  });

/**
 * Translated values for one question in one language. Disabled when no
 * language is selected. Returns the full payload — callers build their
 * own response_id / participant_id indexes from `data.translations`.
 */
export const useQuestionTranslations = (surveyId, questionId, language) =>
  useQuery({
    queryKey: ['question-translations', surveyId, questionId, language],
    queryFn: () => fetchQuestionTranslations(surveyId, questionId, language),
    enabled: !!surveyId && !!questionId && !!language,
    staleTime: 5 * 60 * 1000,
  });

export const useFetchQuestionCodeList = (surveyId, questionId, isAnalyzed) => {
  return useQuery({
    queryKey: ['survey-question-code-list', surveyId, questionId],
    queryFn: async () => {
      const response = await fetchQuestionCodes(surveyId, questionId);
      const convertedData = response?.codes.map((code) => ({
        definition: code.definition,
        summary: code.summary,
        code_name: code.code,
        sources: code.sources,
        sailency: code.sources?.length || 0,
        sources_count: code.sources_count,
      }));
      return convertedData;
    },
    enabled: !!surveyId && !!questionId && isAnalyzed,
  });
};

export const useFetchQuestionSentiment = (surveyId, questionId, isAnalyzed) => {
  return useQuery({
    queryKey: ['survey-question-sentiment', surveyId, questionId],
    queryFn: async () => {
      const response = await fetchQuestionCodes(surveyId, questionId);

      const analyzeSentimentWithCodeDetails = (rawData) => {
        // Create code lookup map with metadata
        const codeMetadata = _.keyBy(rawData, 'code');

        // Process and deduplicate sources
        const processed = _(rawData)
          .flatMap((item) =>
            item.sources.map((source) => ({
              code: item.code,
              text: source.text,
              sentiment: source.sentiment,
            })),
          )
          .groupBy((item) => `${item.text}|${item.sentiment}`)
          .map((group) => {
            const codes = _.uniq(_.map(group, 'code'));
            return {
              text: group[0].text,
              sentiment: group[0].sentiment,
              codes: codes.map((code) => ({
                name: code,
                definition: codeMetadata[code].definition,
                summary: codeMetadata[code].summary,
                saliency: _.filter(group, { code }).length,
                sources: codeMetadata[code].sources,
                color: codeMetadata[code].code_color,
              })),
              code_count: codes.length,
            };
          })
          .groupBy('sentiment')
          .mapValues((sources) => ({
            count: sources.length,
            sources,
            // Overall code summary ranked by salience
            codes: _(sources)
              .flatMap('codes')
              .groupBy('name')
              .mapValues((codeInstances) => ({
                name: codeInstances[0].name,
                definition: codeInstances[0].definition,
                summary: codeInstances[0].summary,
                saliency: _.sumBy(codeInstances, 'saliency'),
                appears_in_sources: codeInstances.length,
              }))
              .values()
              .orderBy('saliency', 'desc')
              .value(),
          }))
          .value();

        // Ensure all sentiments are represented
        const allSentiments = ['positive', 'negative', 'neutral'];
        return {
          ..._.fromPairs(
            allSentiments.map((s) => [s, processed[s] || { count: 0, sources: [], codes: [] }]),
          ),
          summary: {
            original_count: _(rawData).sumBy('sources_count'),
            deduplicated_count: _(processed).values().sumBy('count'),
            duplicates_removed:
              _(rawData).sumBy('sources_count') - _(processed).values().sumBy('count'),
            most_salient_codes: _(processed)
              .values()
              .flatMap('codes')
              .orderBy('salliency', 'desc')
              .take(5)
              .value(),
          },
        };
      };

      // Usage example:
      const result = analyzeSentimentWithCodeDetails(response?.codes);

      // Usage example:
      return result;
    },
    enabled: !!surveyId && !!questionId && isAnalyzed,
  });
};
export const useFetchQuestionSentimentStat = (surveyId, questionId, isAnalyzed) => {
  return useQuery({
    queryKey: ['survey-question-sentiment-stat', surveyId, questionId],
    queryFn: async () => {
      const response = await fetchQuestionSentimentStat(surveyId, questionId);

      return response;
    },
    enabled: !!surveyId && !!questionId && isAnalyzed,
  });
};

export const useFetchQuestionCodeDetails = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, questionId, code }) => {
      const queryKey = ['survey-question-code-list', id, questionId];

      // First try to get from cache
      let data = queryClient.getQueryData(queryKey);

      // If no cache, fetch from API
      if (!data) {
        try {
          data = await queryClient.fetchQuery({
            queryKey,
            queryFn: () => fetchQuestionCodes(id, questionId),
          });
          // Transform the data to match expected format
          data =
            data?.codes?.map((item) => ({
              definition: item.definition,
              summary: item.summary,
              code_name: item.code,
              sources: item.sources,
              sailency: item.sources?.length || 0,
              sources_count: item.sources_count,
            })) || [];
        } catch (error) {
          console.error('Error fetching question code details:', error);
          return null;
        }
      }

      // Filter the data based on the code
      const filteredData = Array.isArray(data)
        ? data.find((item) => item.code_name === code)
        : null;

      return filteredData ? { ...filteredData } : null;
    },
  });
};

export const useFetchQuestionTheme = (surveyId, questionId) => {
  return useQuery({
    queryKey: ['survey-question-theme', surveyId, questionId],
    queryFn: async () => {
      const response = await fetchQuestionTheme(surveyId, questionId);
      const themes = response?.themes?.map((theme, themeIndex) => {
        const baseHue = (themeIndex * 320) % 360;
        const themeColor = generateThemeColor(themeIndex, response?.themes.length, baseHue);
        return {
          ...theme,
          color: themeColor,
        };
      });

      return themes;
    },
    enabled: !!surveyId && !!questionId,
  });
};

function generateThemeColor(index, total) {
  const hue = (530 + index * (360 / total)) % 360;
  return `hsl(${hue}, 140%,30%)`;
}

export const useFetchNextAnalysis = (studyId) => {
  return useQuery({
    queryKey: ['next-analysis', studyId],
    queryFn: () => fetchNextAnalysis(studyId),
    enabled: !!studyId,
  });
};

export const useFetchSharedStudies = () => {
  return useQuery({
    queryKey: ['shared-studies'],
    queryFn: () => fetchSharedStudies(),
  });
};

export const useFetchWorkspaces = ({ isAuthenticated }) => {
  return useQuery({
    queryKey: ['workspaces'],
    queryFn: getWorkspaces,
    enabled: isAuthenticated,
  });
};

export const useFetchStudyReports = ({ studyId, paginationModel, type }) => {
  return useQuery({
    queryKey: ['study-reports', studyId, paginationModel, type],
    queryFn: () => fetchStudyReports({ id: studyId, paginationModel }),
    enabled: !!studyId,
  });
};
